/**
 * Is this mailbox's connection actually proven?
 *
 * Deliberately NOT `status === 'verified'`. The backend recomputes that field
 * as "SMTP works" -- `services/sender_email.py` sets status to 'verified' when
 * SMTP alone passes and parks the IMAP error in `verification_error`. So a
 * mailbox that can send but cannot read replies still reads as 'verified', and
 * a badge or a disabled button keyed off it would be telling the user
 * everything is fine while reply detection is dead.
 *
 * That distinction became load-bearing when a refused IMAP login started
 * demoting `imap_verified` on its own (services/mailbox_monitor.py): Verify is
 * the only way back, so anything that hides or disables it must be certain the
 * mailbox has nothing left to fix.
 */
import type { SenderEmail } from '@/types/senderEmail'

/** The subset this predicate needs — so callers can pass a partial row. */
export interface VerifiableSender {
  connection_type?: string
  status?: string
  smtp_verified?: boolean
  imap_verified?: boolean
}

/**
 * True only when both halves of an SMTP mailbox are proven.
 *
 * An OAuth mailbox never runs the manual verify step at all -- the grant is
 * the proof -- so it counts as verified on `status` alone. `imap_verified` is
 * never set on those rows, and requiring it would leave them permanently
 * showing an action that does nothing for them.
 */
export function isFullyVerified(sender: VerifiableSender | SenderEmail): boolean {
  const s = sender as VerifiableSender
  if (s.connection_type && s.connection_type !== 'smtp') {
    return s.status === 'verified'
  }
  return Boolean(s.smtp_verified) && Boolean(s.imap_verified)
}

/**
 * True when sending works but reading replies does not.
 *
 * This is the state `status === 'verified'` hides, and the reason Verify stays
 * available: the mailbox is half-working and the user needs the control that
 * fixes it.
 */
export function isSendOnlyVerified(sender: VerifiableSender | SenderEmail): boolean {
  const s = sender as VerifiableSender
  if (s.connection_type && s.connection_type !== 'smtp') return false
  return Boolean(s.smtp_verified) && !s.imap_verified
}
